import React from 'react';
import PageHero from './PageHero';

interface MessageSection {
  title: string;
  content: string;
  icon?: number;
}

interface LeadershipMessageProps {
  messageType: 'Director' | 'Principal' | 'Vice Principal';
  name: string;
  title: string;
  quote?: string;
  image?: string;
  sections: MessageSection[];
  closingMessage?: string;
  gradientColors?: {
    from: string;
    to: string;
  };
  heroGradientColors?: {
    from: string;
    via?: string;
    to: string;
  };
}

const LeadershipMessage: React.FC<LeadershipMessageProps> = ({
  messageType,
  name,
  title,
  quote,
  image,
  sections,
  closingMessage,
  gradientColors = {
    from: 'from-blue-600',
    to: 'to-purple-600'
  },
  heroGradientColors = {
    from: 'from-blue-600',
    via: 'via-indigo-600',
    to: 'to-violet-600'
  }
}) => {
  const styles = {
    border: gradientColors ? `border-${gradientColors.from.split('-')[1]}-500` : 'border-blue-500'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <PageHero
        title={`${messageType}'s Message`}
        subtitle={name}
        gradientColors={heroGradientColors}
        titleGradient={{
          from: 'from-white',
          to: 'to-blue-100'
        }}
        subtitleColor="text-blue-100"
      />

      <div className="container mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Image Section */}
            {image && (
              <div className="lg:w-1/3">
                <img
                  src={image}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content Section */}
            <div className="lg:w-2/3 p-2 lg:p-4">
              <div className="prose prose-lg max-w-none">
                {quote && (
                  <blockquote className={`border-l-4 ${styles.border} pl-4 italic text-gray-700 mb-8`}>
                    "{quote}"
                  </blockquote>
                )}

                {sections.map((section, index) => (
                  <div key={index} className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      {section.title}
                    </h3>
                    <div className="text-gray-700 leading-relaxed">
                      {section.content.split('\n\n').map((paragraph, pIndex) => (
                        <p key={pIndex} className={pIndex > 0 ? 'mt-4' : ''}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}

                {closingMessage && (
                  <div className={`bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-2xl border-l-4 ${styles.border}`}>
                    <div className="text-gray-700 leading-relaxed">
                      {closingMessage.split('\n\n').map((paragraph, index) => (
                        <p key={index} className={index > 0 ? 'mt-4' : ''}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom decorative elements */}
        <div className="mt-12 flex justify-center space-x-4">
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
          <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '100ms' }}></div>
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
        </div>
      </div>
    </div>
  );
};

export default LeadershipMessage;